import ApiRequests from '../http/ApiRequests';
import { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import axiosErrorHandler from '../http/AxiosErrorHandler';
import useConfirmDialog from './useConfirmDialog';

const useTariffApproval = () => {
    const api = useMemo(() => new ApiRequests(), []);
    const [selectedTariffRow, setSelectedTariffRow] = useState(null);
    const [pendingTariffGridData, setPendingTariffGridData] = useState([]);
    const [generalErrorList, setGeneralErrorList] = useState([]);
    const [successMessage, setSuccessMessage] = useState(null);

    const confirm = useConfirmDialog();

    const pendingTariffHeaders = [
        { name: 'File Name', key: 'name' },
        { name: 'Uploaded By', key: 'uploadedBy' },
        { name: 'Upload Date Time', key: 'uploadedTs', formatter: 'datetime' },
    ];

    const pendingTariffContentHeaders = [
        { name: 'Category', key: 'category' },
        { name: 'Product/Service', key: 'productService' },
        { name: 'Type', key: 'type' },
        { name: 'Description', key: 'description' },
        { name: 'Tariff For', key: 'tariffFor' },
        { name: 'Tariff Line (description)', key: 'tariffLineDescription' },
        { name: 'Currency', key: 'currencyCode' },
        { name: 'Tariff Answer', key: 'tariffAnswer' },
        { name: 'Tariff Point', key: 'tariffPoint' },
        { name: 'Last Updated', key: 'lastUpdated' },
    ];

    const getPendingTariffData = useCallback(() => {
        api.getAllPendingTariffData()
            .then(res => setPendingTariffGridData(processData(get(res, 'data', []))))
            .catch(err => {
                const [errorList] = axiosErrorHandler(err);
                setGeneralErrorList(errorList);
            });
    }, [api]);

    useEffect(() => {
        getPendingTariffData();
    }, [getPendingTariffData]);

    const processData = data => {
        return data.map(item => ({
            id: item.id,
            name: item.name,
            uploadedTs: item.uploadedTs,
            uploadedBy: item.uploadedBy,
            content: item.content,
        }));
    };

    const onRowToggle = expandedData => {
        setSelectedTariffRow(expandedData.data);
    };

    const approveTariffHandler = selectedTariff => {
        const acceptFunc = async () => {
            try {
                await api.approvePendingTariff(selectedTariff.id);
                setSuccessMessage({ success: 'Successfully approved changes' });
                getPendingTariffData();
            } catch (err) {
                const [errorList] = axiosErrorHandler(err);
                setGeneralErrorList(errorList);
            }
        };

        const conformProps = {
            message: `Are you sure you want to approve selected tariff ?`,
            header: 'Confirm Approve',
            icon: 'pi pi-plus-circle',
            accept: () => acceptFunc(),
            reject: () => {},
        };

        confirm(conformProps);
    };

    const rejectTariffHandler = selectedTariff => {
        const acceptFunction = async () => {
            try {
                await api.rejectPendingTariff(selectedTariff.id);
                setSuccessMessage({ success: 'Successfully rejected changes' });
                getPendingTariffData();
            } catch (err) {
                const [errorList] = axiosErrorHandler(err);
                setGeneralErrorList(errorList);
            }
        };

        const conformProps = {
            message: `Are you sure you want to reject and delete selected tariff ?`,
            header: 'Confirm Approve',
            icon: 'fa fa-trash-o',
            accept: () => acceptFunction(),
            reject: () => {},
        };

        confirm(conformProps);
    };

    return {
        pendingTariffHeaders,
        pendingTariffContentHeaders,
        pendingTariffGridData,
        onRowToggle,
        selectedTariffRow,
        approveTariffHandler,
        rejectTariffHandler,
        successMessage,
        generalErrorList,
    };
};

export default useTariffApproval;
